import React from "react";

import { CreditCard, Edit } from "lucide-react";

import { renderSalesConditionLabel } from "~/components/SalesConditionSelector";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { SalesCondition } from "~/types/client";
import { formatDate } from "~/utils/helpers";

export type ClientRiskData = {
  id: string;
  name: string;
  company: string;
  taxId: string;
  email: string;
  insuredAmount: number;
  trailingPaymentDays: number;
  createdAt: string;
  salesConditionId?: string;
};

interface ClientRiskTableProps {
  clients: ClientRiskData[];
  showActions: boolean;
  onEditClient?: (client: ClientRiskData) => void;
  isLoading: boolean;
}

const ClientRiskTable: React.FC<ClientRiskTableProps> = ({
  clients,
  showActions,
  onEditClient,
  isLoading,
}) => {
  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString("es-CL")}`;
  };

  const { salesConditions } = useSalesConditions();

  // Helper function to get sales condition by ID
  const getSalesCondition = (id?: string): SalesCondition | undefined => {
    if (!id) return undefined;
    return salesConditions.find((condition) => condition.id === id);
  };

  if (isLoading) {
    return <TableSkeleton rows={6} columns={7} />;
  }

  if (clients.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>RUT</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Fecha Creación</TableHead>
            <TableHead>Monto Asegurado</TableHead>
            <TableHead>Días de Pago</TableHead>
            <TableHead>Condición de Venta</TableHead>
            {showActions && (
              <TableHead className="text-right">Acciones</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((client, index) => {
            const salesCondition = getSalesCondition(client.salesConditionId);

            return (
              <TableRow
                key={client.id}
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              >
                <TableCell className="font-medium">{client.name}</TableCell>
                <TableCell>{client.taxId}</TableCell>
                <TableCell>{client.email}</TableCell>
                <TableCell>{formatDate(client.createdAt)}</TableCell>
                <TableCell>{formatCurrency(client.insuredAmount)}</TableCell>
                <TableCell>{client.trailingPaymentDays}</TableCell>
                <TableCell>
                  {salesCondition ? (
                    <Badge
                      className="flex items-center gap-1"
                      variant="outline"
                    >
                      <CreditCard className="h-3 w-3" />
                      {renderSalesConditionLabel(salesCondition)}
                    </Badge>
                  ) : (
                    <span className="text-sm text-gray-500">No asignada</span>
                  )}
                </TableCell>
                {onEditClient && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditClient(client)}
                    >
                      <Edit className="mr-1 h-4 w-4" /> Editar
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ClientRiskTable;
